import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import PagePremission from '@/routes/Restrictions/PagePremission';
import { lazyRouteComponent, preloadComponent } from '@/utils/lazyLoad';
import MonitoringPermission from '@/utils/premissions/MonitoringPermission.enum';
import Premission from '@/utils/premissions/premission.type';

// Optimized lazy loading with performance enhancements
const SalesInvoicesPage = lazyRouteComponent(
  () => import('@/pages/salesInvoices'),
);
const NewSaleInvoicesPage = lazyRouteComponent(
  () => import('@/pages/NewSalesInvoice'),
);
const LiveTerminalPage = lazyRouteComponent(
  () => import('@/pages/salesInvoices/LiveTerminal'),
);

// Preload the most frequently accessed page (new sales invoice) for instant transitions
// This is critical since it's the default route
preloadComponent(() => import('@/pages/NewSalesInvoice'));

// Define routes with optimized components
const SalesInvoicesRoutes: RouteObject[] = [
  {
    index: true,
    element: <Navigate to="/sales-invoices/new" replace />,
  },
  {
    path: 'new',
    element: (
      <PagePremission prem={Premission.Prem_Sales_Invoices_Create}>
        <NewSaleInvoicesPage />
      </PagePremission>
    ),
  },
  {
    path: 'all',
    element: (
      <PagePremission prem={Premission.Prem_Sales_Invoices_Open}>
        <SalesInvoicesPage />
      </PagePremission>
    ),
  },
  {
    path: 'live',
    element: (
      <PagePremission prem={MonitoringPermission.FULL_ACCESS}>
        <LiveTerminalPage />
      </PagePremission>
    ),
  },
];

// Advanced preloading API for dynamic route prefetching based on user behavior
export const prefetchSalesRoutes = {
  prefetchNew: () => void import('@/pages/NewSalesInvoice'),
  prefetchAll: () => void import('@/pages/salesInvoices'),
  prefetchBoth: () => {
    void import('@/pages/NewSalesInvoice');
    void import('@/pages/salesInvoices');
  },
};

export default SalesInvoicesRoutes;
